import { useEffect, useRef, useState } from 'react';

interface Testimonial {
  text: string;
  author: string;
  company: string;
  rating: number;
}

interface PortfolioItem {
  image: string;
  label: string;
  location: string;
}

interface TestimonialsSectionProps {
  testimonials: Testimonial[];
  title?: string;
  subtitle?: string;
  portfolio?: PortfolioItem[];
}

function useReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

export default function TestimonialsSection({
  testimonials,
  title = 'Client Proof & Portfolio',
  subtitle = 'Trusted by automotive brands, event agencies, and dealer groups who demand results at every scale.',
  portfolio,
}: TestimonialsSectionProps) {
  const { ref, visible } = useReveal();
  const [active, setActive] = useState(0);

  return (
    <section
      ref={ref}
      className="py-28 px-6 lg:px-12 xl:px-16"
      style={{ background: 'linear-gradient(180deg, #0d0d0d 0%, #0A0A0A 100%)' }}
    >
      <div className="max-w-7xl mx-auto">
        <div className={`mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="w-10 h-px bg-gold" />
            <span className="text-gold text-[10px] font-sans tracking-[0.35em] uppercase font-semibold">Client Proof</span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <h2 className="font-serif text-white text-4xl lg:text-5xl font-bold leading-tight max-w-lg">{title}</h2>
            <p className="text-white/40 text-base font-sans max-w-md lg:text-right">{subtitle}</p>
          </div>
        </div>

        {/* Portfolio Grid */}
        {portfolio && portfolio.length > 0 && (
          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 mb-16 transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {portfolio.map((item, i) => (
              <div
                key={i}
                className="relative group overflow-hidden cursor-pointer"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div className="w-full h-48 overflow-hidden">
                  <img
                    src={item.image}
                    alt={item.label}
                    className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                  <div className="text-white text-xs font-sans font-semibold">{item.label}</div>
                  <div className="text-gold/60 text-[10px] font-sans mt-0.5">{item.location}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Featured testimonial */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
          <div
            className="relative p-10 lg:p-14 border border-gold/15"
            style={{ background: 'linear-gradient(135deg, #0f0f0f 0%, #111009 100%)' }}
          >
            <div className="absolute top-8 left-10 font-serif text-gold/8 text-[120px] leading-none select-none pointer-events-none" aria-hidden="true">
              &ldquo;
            </div>
            <div className="relative z-10">
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[active].rating)].map((_, i) => (
                  <div key={i} className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-gold text-sm" />
                  </div>
                ))}
              </div>
              <p className="font-serif text-white text-xl lg:text-2xl leading-relaxed mb-8 max-w-4xl">
                &ldquo;{testimonials[active].text}&rdquo;
              </p>
              <div className="flex items-center gap-4">
                <div className="w-10 h-px bg-gold/40" />
                <div>
                  <div className="text-white text-sm font-sans font-semibold">{testimonials[active].author}</div>
                  <div className="text-white/35 text-xs font-sans mt-0.5">{testimonials[active].company}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Selector */}
        <div className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {testimonials.map((t, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`text-left p-4 border transition-all duration-200 cursor-pointer ${
                active === i ? 'border-gold/50 bg-gold/8' : 'border-white/8 bg-[#0d0d0d] hover:border-white/20'
              }`}
            >
              <div className={`text-xs font-sans font-semibold mb-1 transition-colors ${active === i ? 'text-gold' : 'text-white/60'}`}>
                {t.author}
              </div>
              <div className="text-white/30 text-[11px] font-sans leading-snug truncate">{t.company}</div>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}
